import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CrudAction } from 'enums/tables';
import { t } from 'i18next';
import { WithID } from 'lib_ts/interfaces/common/i-generics';
import {
  IHitter,
  IHitterStats,
  IHitterUpsert,
} from 'lib_ts/interfaces/i-hitter';
import { clone, differenceBy } from 'lodash';
import { HittersService } from 'services/hitters.service';
import { create } from 'zustand';

const uploadHitterAvatar = async (
  hitterID: string,
  file: File
): Promise<IHitter | undefined> => {
  const formData = new FormData();
  formData.append('files', file);

  const updatedHitter = await HittersService.getInstance().uploadHitterAvatar(
    hitterID,
    formData
  );

  return updatedHitter;
};

interface ICrudConfig {
  key: number;
  action: CrudAction;
  models: IHitter[];
  onClose?: () => void;
}

interface State {
  loading: boolean;
  lastChanged: number;

  hitters: IHitter[];
  activeHitter: IHitter | undefined;

  filters: Partial<IHitter>;
  filtered: IHitter[];

  dialogCRUD: ICrudConfig | undefined;

  // TODO: determine if anyone uses this; if not, remove it
  // key is the hitter ID, value is the hitter's stats for this session
  stats: Record<string, IHitterStats>;

  // key is the hitter ID, value is true if the hitter has seen at least one pitch this session
  touched: Record<string, boolean>;
}

interface Actions {
  readonly activate: (hitter_id: string) => Promise<void>;
  readonly create: (
    payload: IHitterUpsert,
    onCreate?: (value: IHitter) => void,
    customAvatarFile?: File
  ) => Promise<boolean>;
  readonly delete: (ids: string[]) => Promise<boolean>;
  readonly update: (config: {
    payload: WithID<IHitterUpsert>;
    onUpdate?: (value: IHitter) => void;
    customAvatarFile?: File;
    silently?: boolean;
  }) => Promise<boolean>;

  // TODO: determine if anyone uses this; if not, remove it
  readonly increasePitchCount: (hitter_id: string) => IHitterStats;

  readonly uploadCsv: (file: File) => Promise<IHitter[] | undefined>;
}

type Store = State & Actions;

const useHittersStore = create<Store>((set, get) => ({
  loading: false,
  lastChanged: Date.now(),
  hitters: [],
  activeHitter: undefined,
  filters: {},
  filtered: [],
  dialogCRUD: undefined,
  stats: {},
  touched: {},

  activate: async (hitter_id) => {
    if (!hitter_id) {
      set({ activeHitter: undefined });
      return;
    }

    // immediately update the active hitter in the store so the selection feels fast
    set((prev) => ({
      activeHitter: prev.hitters.find((h) => h._id === hitter_id),
    }));

    // then update the hitter in the database
    const updatedHitter = await HittersService.getInstance().updateHitter({
      _id: hitter_id,
      lastActive: new Date().toISOString(),
    });

    // then replace the hitter in the store
    set((prev) => ({
      hitters: prev.hitters.map((h) =>
        h._id === hitter_id ? updatedHitter : h
      ),
      activeHitter: updatedHitter,
    }));
  },

  create: async (payload, onCreate, customAvatarFile?: File) => {
    try {
      set({ loading: true });

      let newHitter = await HittersService.getInstance().createHitter(payload);

      // in case we're creating a new hitter, we'll need to upload the avatar after we have the hitter ID
      if (customAvatarFile) {
        newHitter =
          (await uploadHitterAvatar(newHitter._id, customAvatarFile)) ||
          newHitter; // make TS stop complaining
      }

      set((prev) => ({
        hitters: [...prev.hitters, newHitter],
      }));

      NotifyHelper.success({ message_md: 'Hitter created!' });

      if (onCreate) {
        onCreate(newHitter);
      }

      return true;
    } catch (e) {
      console.error(e);

      NotifyHelper.error({
        message_md: t('common.request-failed-msg'),
      });

      return false;
    } finally {
      set({ loading: false });
    }
  },

  delete: async (ids: string[]) => {
    try {
      set({ loading: true });

      const success = await HittersService.getInstance()
        .deleteHitters(ids)
        .finally(() => {
          set({ loading: false });
        });

      if (!success) {
        throw new Error('failed to delete hitters');
      }

      set((prev) => ({
        // auto close the delete confirmation dialog
        dialogCRUD: undefined,
        // remove the deleted players from the store
        hitters: prev.hitters.filter((v) => !ids.includes(v._id)),
      }));

      NotifyHelper.success({
        message_md:
          ids.length === 1
            ? 'Hitter deleted.'
            : `${ids.length} hitters deleted.`,
      });

      return success;
    } catch (e) {
      console.error(e);

      NotifyHelper.error({
        message_md: t('common.request-failed-msg'),
      });

      return false;
    }
  },

  update: async (config) => {
    try {
      const { payload, customAvatarFile, silently, onUpdate } = config;

      set({ loading: true });

      let updatedHitter =
        await HittersService.getInstance().updateHitter(payload);

      // need to upload file to S3 and update hitter avatar_path
      if (customAvatarFile) {
        updatedHitter =
          (await uploadHitterAvatar(updatedHitter._id, customAvatarFile)) ||
          updatedHitter; // make TS stop complaining
      }

      set((prev) => ({
        activeHitter:
          prev.activeHitter?._id === updatedHitter._id
            ? updatedHitter
            : prev.activeHitter,
        hitters: prev.hitters.map((h) =>
          h._id === updatedHitter._id ? updatedHitter : h
        ),
      }));

      if (!silently) {
        NotifyHelper.success({
          message_md: `${updatedHitter.name} was updated successfully.`,
        });
      }

      if (onUpdate) {
        onUpdate(updatedHitter);
      }

      return true;
    } catch (e) {
      console.error(e);

      NotifyHelper.error({
        message_md: t('common.request-failed-msg'),
      });

      return false;
    } finally {
      set({ loading: false });
    }
  },

  increasePitchCount: (hitter_id) => {
    const nextStats = get().stats[hitter_id] || {
      pitches: 0,
      swings: 0,
      hits: 0,
    };

    const isFirstTouch = [
      nextStats.hits,
      nextStats.pitches,
      nextStats.swings,
    ].every((v) => v === 0);

    nextStats.pitches++;

    set((prev) => ({
      stats: {
        ...prev.stats,
        [hitter_id]: nextStats,
      },
      // ? don't change touched if this is not the first touch by this hitter
      touched: isFirstTouch
        ? {
            ...prev.touched,
            [hitter_id]: true,
          }
        : prev.touched,
    }));

    return clone(nextStats);
  },

  uploadCsv: async (file) => {
    set({ loading: true });

    /** append the file */
    const formData = new FormData();
    formData.append('files', file);

    const upsertedHitters = await HittersService.getInstance()
      .uploadCsv(formData)
      .finally(() => set({ loading: false }));

    if (!upsertedHitters || upsertedHitters.length === 0) {
      NotifyHelper.error({
        message_md: t('common.request-failed-msg'),
      });
      return undefined;
    }

    // add the new hitters to the end of the list
    set((prev) => ({
      hitters: [
        ...prev.hitters,
        ...differenceBy(upsertedHitters, prev.hitters, '_id'),
      ],
    }));

    return upsertedHitters;
  },
}));

export default useHittersStore;
